/* ═════════════════════════ AuthGate ═════════════════════════════════
   Top-level wrapper. On mount, it calls /api/auth/me which returns one of:
     - { state: "anon" }                            → render <Login/>
     - { state: "active", user }                    → render <App/> with AuthProvider
     - { state: "pending", pending, request|null }  → render <Onboarding/>

   Multi-workspace SaaS: a signed-in user who isn't yet in any workspace can
   always choose to create a new workspace (becoming its admin) OR to request
   to join an existing one. If they've already submitted a join-request,
   `request` is populated and Onboarding shows the waiting-for-approval view.

   Any descendant can call useAuth() to read { user, logout, refresh }.
   ═══════════════════════════════════════════════════════════════════ */
import { useCallback, useEffect, useState } from "react";
import App from "./App.jsx";
import Login from "./Login.jsx";
import Onboarding from "./Onboarding.jsx";
import { AuthProvider } from "./useAuth.js";

// Persist the post-checkout session id across the Google sign-in
// roundtrip. Stripe lands the visitor on /app/?welcome=<id>, but they
// then bounce out to Google and back, which loses the query param —
// so we stash it in localStorage and read on the way back in.
const WELCOME_KEY = "pr:welcome-checkout";
function captureWelcomeFromUrl() {
  try {
    const url = new URL(window.location.href);
    const id = url.searchParams.get("welcome");
    if (id) {
      localStorage.setItem(WELCOME_KEY, id);
      url.searchParams.delete("welcome");
      window.history.replaceState({}, "", url.toString());
    }
  } catch { /* ignore */ }
}
function readWelcome() {
  try { return localStorage.getItem(WELCOME_KEY) || null; } catch { return null; }
}
function clearWelcome() {
  try { localStorage.removeItem(WELCOME_KEY); } catch { /* ignore */ }
}

export default function AuthGate() {
  const [state, setState] = useState({ status: "loading" });
  const [claimError, setClaimError] = useState("");
  const [claiming, setClaiming] = useState(false);
  // Strip ?welcome=<id> from the URL on first mount, persist it.
  useEffect(() => { captureWelcomeFromUrl(); }, []);

  const refresh = useCallback(async () => {
    try {
      const r = await fetch("/api/auth/me", { credentials: "same-origin" });
      // 401 bodies still carry { state: "anon" } — parse regardless so we
      // can distinguish no-cookie from pending-session-without-team.
      const data = await r.json().catch(() => ({ state: "anon" }));
      if (r.ok && data.state === "active") {
        setState({ status: "authed", user: data.user });
      } else if (data.state === "pending") {
        setState({ status: "pending", pending: data.pending, request: data.request || null });
      } else {
        setState({ status: "anon" });
      }
    } catch {
      setState({ status: "anon" });
    }
  }, []);

  const logout = useCallback(async () => {
    try { await fetch("/api/auth/logout", { method: "POST", credentials: "same-origin" }); } catch {}
    setState({ status: "anon" });
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Authed + welcome token still pending? Block App from mounting,
  // run the claim, set the "freshly created" flags, then refresh.
  // Without the await App reads localStorage before the flags land
  // and ends up on Plan & Billing instead of the welcome picker.
  useEffect(() => {
    if (state.status !== "authed") return;
    if (claiming) return;
    const welcomeId = readWelcome();
    if (!welcomeId) return;
    setClaiming(true);
    (async () => {
      try {
        const r = await fetch("/api/onboarding/claim-checkout", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "same-origin",
          body: JSON.stringify({ sessionId: welcomeId }),
        });
        if (r.ok) {
          try {
            localStorage.setItem("pr:just-created-team", "1");
            localStorage.removeItem("pr:page");
          } catch { /* ignore */ }
        } else {
          const d = await r.json().catch(() => ({}));
          setClaimError(d.error || "Couldn't apply your subscription — open Settings → Plan & Billing.");
        }
      } catch (e) {
        setClaimError(e.message);
      } finally {
        clearWelcome();
        setClaiming(false);
        refresh();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.status]);

  if (state.status === "loading" || claiming) {
    return (
      <div style={{
        minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        background: "#F4F4F5", fontFamily: "Inter, sans-serif", color: "#9CA3AF", fontSize: 14, gap: 14,
      }}>
        <Spinner />
        {claiming && <div style={{ fontSize: 13, color: "#6B7280" }}>Activating your subscription…</div>}
      </div>
    );
  }

  // If they came back from Stripe with a welcome token but aren't yet
  // signed in, surface a friendlier login screen so they know they're
  // mid-flow. The welcome token persists through Google sign-in via
  // localStorage and is claimed once they finish onboarding.
  const welcomeId = readWelcome();
  if (state.status === "anon")    return <Login welcomeFromCheckout={!!welcomeId} />;
  if (state.status === "pending") return <Onboarding pending={state.pending} request={state.request} onChange={refresh} onLogout={logout} welcomeFromCheckout={!!welcomeId} />;

  const isDemo = !!state.user?.isDemo;

  return (
    <AuthProvider value={{ user: state.user, refresh, logout }}>
      {isDemo && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, zIndex: 99999,
          background: "#000", color: "#fff",
          display: "flex", alignItems: "center", justifyContent: "center", gap: 16,
          padding: "10px 20px", fontSize: 13, fontFamily: "Inter, sans-serif",
          fontWeight: 500, letterSpacing: "-0.01em",
        }}>
          <span style={{ opacity: 0.5 }}>◆</span>
          <span>You're viewing a live demo of Elion Roaster.</span>
          <a href="/pricing" style={{ color: "#fff", fontWeight: 700, textDecoration: "underline", textUnderlineOffset: 3 }}>See pricing →</a>
        </div>
      )}
      {claimError && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, zIndex: 99998,
          background: "#FEF2F2", color: "#991B1B",
          padding: "10px 20px", fontSize: 13, fontFamily: "Inter, sans-serif",
          borderBottom: "1px solid #FCA5A5", textAlign: "center",
        }}>{claimError}</div>
      )}
      <div style={isDemo ? { paddingTop: 41 } : undefined}>
        <App isDemo={isDemo} />
      </div>
    </AuthProvider>
  );
}

function Spinner() {
  return (
    <div style={{
      width: 28, height: 28, borderRadius: "50%",
      border: "3px solid #E4E4E7", borderTopColor: "#000000",
      animation: "spin 0.8s linear infinite",
    }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
